import { useMemo } from "react";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { useDealActions, useDealIdSelector } from "store";
import { IApiServiceError } from "store/services";
import { IInvestmentDetailField, IInvestmentDetailValue } from "store/types";

import { currenciesArray } from "../../../../constants";

export const useAddCompanyHook = (columns: IInvestmentDetailField[], onClose: () => void, refetch: () => void) => {
  const dealId = useDealIdSelector();
  const { createInvestmentDetailAction } = useDealActions();

  const initialValues: IInvestmentDetailValue[] = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        value: "",
        date: null,
        currency: column.with_history ? currenciesArray[0] : null
      })),
    [columns]
  );

  const companyNameId = columns.find((col) => col.name === "Company Name")?.id;
  const dateRequired = columns.filter((col) => col.with_history).map((col) => col.id);
  const numericRequired = columns.filter((col) => col.column_type === "number").map((col) => col.id);

  const formik = useFormik<IInvestmentDetailValue[]>({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.array().of(
      Yup.object().shape({
        id: Yup.number(),
        value: Yup.string()
          .when("id", {
            is: companyNameId,
            then: (schema) => schema.required("Company name is required"),
            otherwise: (schema) => schema
          })
          .when("id", {
            is: (id: number) => numericRequired.includes(id),
            then: (schema) => schema.min(0, "Value must be greater than 0"),
            otherwise: (schema) => schema
          }),
        date: Yup.string()
          .nullable()
          .when(["id", "value"], {
            is: (id: number, value: string) => dateRequired.includes(id) && value,
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.nullable()
          }),
        currency: Yup.string().nullable()
      })
    ),
    onSubmit: (values) => {
      if (!dealId) return;
      const filteredValues = values.filter((field) => !!field.value);
      try {
        createInvestmentDetail({
          dealId,
          values: filteredValues
        });
      } catch (error) {
        console.error("Submit error:", error);
      }
    }
  });

  const { handleSubmit, setFieldValue, values, errors, touched, resetForm, setFieldError } = formik;

  const { action: createInvestmentDetail } = useActionHook(createInvestmentDetailAction, {
    onSuccess: () => {
      resetForm();
      onClose();
      refetch();
    },
    onReject: (error: IApiServiceError) => {
      // This is to parse bad errors from the server.
      // Example: {
      //   "message": "Validation error",
      //   "errors": {
      //     "data.0.value": ["Company name already exists."]
      //   }
      // }

      // Parse API errors and convert to Formik format
      const apiErrors = error?.errors || error?.response?.data?.errors || {};

      Object.keys(apiErrors).forEach((errorPath) => {
        // Convert paths like "data.0.value" to "[0].value" for Formik
        const match = errorPath.match(/^data\.(\d+)\.(.+)$/);
        if (match) {
          const [, index, fieldName] = match;
          const formikPath = `[${index}].${fieldName}`;
          const errorMessage = apiErrors[errorPath]?.[0] || "";
          if (errorMessage) {
            setFieldError(formikPath, errorMessage);
          }
        }
      });
    }
  });

  const getFieldByColumnId = (columnId: number) => values.find((field) => field.id === columnId) || initialValues[0];

  const handleFieldChange = (columnId: number, updates: Partial<IInvestmentDetailValue>) => {
    const index = values.findIndex((f) => f.id === columnId);
    const field = values[index];
    setFieldValue(`[${index}]`, { ...field, ...updates });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return {
    columns,
    values,
    errors,
    touched,
    handleSubmit,
    handleFieldChange,
    getFieldByColumnId,
    handleClose,
    formik: { values, errors, touched }
  };
};
